import { omit } from 'lodash';

import { IContactGroupAddress } from 'contacts/types';
import { IImportVendorQuoteForm, IImportVendorQuotePayload } from 'quotes/types';

/**
 * The vendor picker's own option carries the Partfiniti contact its supplier email resolves to, so
 * picking a vendor on the import prompt prefills the contact without a request.
 *
 * The form's `contact.id` is a `corporate_user.id` — what `quote[contact_id]` takes. A contact
 * without one cannot carry the quote, so it is no prefill at all (`partfinitiContactId` is null
 * with it). `primaryContact.contactId` is the ERP supplier contact id and is never used here.
 */
export const mapVendorLocationToImportFormContact = (
  vendorLocation?: Partial<IContactGroupAddress> | null
): IImportVendorQuoteForm['contact'] | null => {
  const primaryContact = vendorLocation?.primaryContact;

  if (!primaryContact?.corporateUserId) {
    return null;
  }

  return {
    id: primaryContact.corporateUserId,
    name: primaryContact.contactName,
    companyName: primaryContact.companyName ?? '',
  };
};

// The vendor bag is a sibling of `quote[...]`, never a member of it. An unset vendor omits the bag
// instead of sending `''`: on `/quote/import/start` an empty value is read exactly like an absent
// one, so the BE falls back to the mapping's own vendor location.
export const mapVendorLocationToImportPayload = (
  vendorLocation?: Partial<IContactGroupAddress> | null
): Pick<IImportVendorQuotePayload, 'vendorMapping'> =>
  vendorLocation?.id ? { vendorMapping: { contactGroupAddressId: vendorLocation.id } } : {};

// The duplicate check reuses the whole import payload — as a query string AND as the react-query
// key of the declarative variant. The vendor bag and the prompt-support flag are both finalize-only
// concerns, so dropping them here keeps them out of the dedupe query (where the BE reads neither)
// and stops a re-picked vendor from re-keying (and re-firing) it.
export const omitVendorMappingFromImportPayload = (requestPayload?: IImportVendorQuotePayload) =>
  requestPayload
    ? (omit(
        requestPayload,
        'vendorMapping',
        'importPromptSupported',
        'vendorQuoteNumberPromptSupported'
      ) as IImportVendorQuotePayload)
    : requestPayload;
